import api from '@/api'

/**
 * Streak CRM sync state for the client info page.
 * Backed by GET /v1/streak/clients/:clientId/status and POST /v1/streak/sync/clients.
 */
const emptyStatus = () => ({
  clientId: null,
  orgEnabled: false,
  syncEnabled: false,
  linked: false,
  boxKey: null,
  boxName: null,
  expectedBoxName: null,
  lastSyncedAt: null,
  lastError: null,
  lastErrorAt: null
})

const state = {
  status: emptyStatus(),
  loading: false,
  syncing: false,
  lastResult: null
}

const getters = {
  status: (state) => state.status,
  orgEnabled: (state) => state.status.orgEnabled,
  loading: (state) => state.loading,
  syncing: (state) => state.syncing,
  lastResult: (state) => state.lastResult
}

const mutations = {
  SET_STATUS (state, status) {
    state.status = Object.assign(emptyStatus(), status || {})
  },
  SET_LOADING (state, loading) {
    state.loading = loading
  },
  SET_SYNCING (state, syncing) {
    state.syncing = syncing
  },
  SET_LAST_RESULT (state, result) {
    state.lastResult = result
  },
  RESET (state) {
    state.status = emptyStatus()
    state.lastResult = null
  }
}

const actions = {
  async fetchStatus ({ commit }, clientId) {
    if (!clientId) {
      commit('RESET')
      return null
    }
    commit('SET_LOADING', true)
    try {
      const { data } = await api.streak.clientStatus({ clientId })
      commit('SET_STATUS', data)
      return data
    } catch (err) {
      // Service unavailable or org not configured: hide the panel rather than error
      commit('SET_STATUS', { clientId, orgEnabled: false })
      return null
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async syncClient ({ commit, dispatch }, { clientId, dryRun = false, force = false }) {
    commit('SET_SYNCING', true)
    try {
      const { data } = await api.streak.syncClients({ clientIds: [ clientId ], dryRun, force })
      const result = (data.results || []).find(r => r.clientId === clientId) || null
      commit('SET_LAST_RESULT', result)
      if (result && result.status === 'error') {
        throw new Error(result.error || 'Streak sync failed')
      }
      return result
    } finally {
      commit('SET_SYNCING', false)
      if (!dryRun) {
        dispatch('fetchStatus', clientId)
      }
    }
  },

  clear ({ commit }) {
    commit('RESET')
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
